import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  catalogCategoryKey,
  displayCatalogCategory,
  uniqueCatalogCategories
} from '../../lib/catalogCategories';
import {
  canSubmitNewProductRequest,
  formatNewProductPlacement,
  getNewProductRequestsForSection,
  getNewProductRequestsOutsideSections
} from '../../lib/changeRequestDraft';
import {
  fontSans,
  headerButtonStyle,
  primaryHeaderButtonStyle,
  textActionStyle,
  warmHeaderBg
} from './requestChangesStyles';

const CREATE_NEW_SECTION = '__create_new_section__'; // catalog browse + confirm modal


let catalogInflight = null;
let catalogCache = null;

function catalogImageUrl(product) {
  return product?.imageUrl || product?.image || null;
}

function productCategory(product) {
  return displayCatalogCategory(product?.category);
}

function productDimensions(product) {
  return String(product?.dimensions || product?.size || '').trim();
}

function displayProductName(name) {
  return String(name || '').trim().replace(/-(?=\d)/g, ' ');
}

function ProductIdentity({ name, dimensions }) {
  const size = String(dimensions || '').trim();
  return (
    <div className="request-add-product-identity">
      <div className="request-add-product-card-name">{displayProductName(name)}</div>
      {size ? <div className="request-add-product-card-meta">{size}</div> : null}
    </div>
  );
}

function loadCatalog(url) {
  if (catalogCache) return Promise.resolve(catalogCache);
  if (!url) return Promise.resolve([]);
  if (!catalogInflight) {
    catalogInflight = fetch(url, { method: 'GET', mode: 'cors' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`catalog ${res.status}`))))
      .then((data) => {
        const next = (data?.catalog || []).filter((product) => product && product.showOnPortal !== false);
        if (next.length > 0) catalogCache = next;
        return next;
      })
      .catch(() => [])
      .finally(() => {
        catalogInflight = null;
      });
  }
  return catalogInflight;
}

function ProductThumb({ src, alt, size = 44 }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        backgroundColor: '#ece8df',
        border: '1px solid #e8e0d4',
        overflow: 'hidden'
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </div>
  );
}

function CatalogImage({ src, alt, className }) {
  return (
    <div className={className} aria-hidden={!alt}>
      {src ? (
        <img
          src={src}
          alt={alt || ''}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </div>
  );
}

const fieldLabelStyle = {
  display: 'block',
  fontSize: 12,
  color: '#6b645c',
  fontFamily: fontSans,
  marginBottom: 6
};

const compactInputStyle = {
  height: 40,
  padding: '0 12px',
  border: '1px solid #e8e4dc',
  borderRadius: 2,
  fontSize: 14,
  fontFamily: fontSans,
  color: '#2C2C2C',
  backgroundColor: '#FFFFFF',
  boxSizing: 'border-box'
};

const stepperButtonStyle = {
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: '50%',
  border: '1px solid #2C2C2C',
  backgroundColor: 'transparent',
  color: '#2C2C2C',
  fontSize: 15,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const disabledPrimaryStyle = {
  ...primaryHeaderButtonStyle,
  backgroundColor: '#e8e4dc',
  color: '#8a847c',
  border: '1px solid transparent',
  cursor: 'not-allowed'
};

const modalTitleStyle = {
  fontFamily: fontSans,
  fontSize: 12,
  fontWeight: 600,
  color: '#2C2C2C',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  lineHeight: 1.2
};

function RequestedProductList({ products, onRemove }) {
  if (!products.length) return null;
  return (
    <div className="no-print request-another-product" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {products.map((product) => (
        <div
          key={product.index}
          style={{
            padding: '10px 12px',
            backgroundColor: '#f7f3ea',
            border: '1px solid #ece4d6',
            borderRadius: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <ProductThumb src={catalogImageUrl(product)} alt="" size={40} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 10,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#6b645c',
                fontFamily: fontSans,
                marginBottom: 4
              }}>
                Requested addition
              </div>
              <div style={{ fontSize: 13, color: '#2C2C2C', fontFamily: fontSans, lineHeight: 1.45 }}>
                {product.name}
              </div>
              <div style={{ fontSize: 12, color: '#6b645c', fontFamily: fontSans, marginTop: 3, lineHeight: 1.4 }}>
                {formatNewProductPlacement(product)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRemove(product.index)}
            style={textActionStyle}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

export default function RequestAnotherProduct({
  variant = 'list',
  open = false,
  onClose,
  sectionName,
  sections = [],
  unmatchedOnly = false,
  draft,
  catalog = [],
  catalogUrl = '',
  onAdd,
  onRemove
}) {
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityDraft, setQuantityDraft] = useState('1');
  const [placement, setPlacement] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [fetchedCatalog, setFetchedCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const inputRef = useRef(null);
  const fieldId = `request-another-product-${String(sectionName || 'section').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const existing = unmatchedOnly
    ? getNewProductRequestsOutsideSections(draft, sections)
    : getNewProductRequestsForSection(draft, sectionName);
  const parentCatalog = Array.isArray(catalog) ? catalog : [];
  const products = parentCatalog.length > 0 ? parentCatalog : fetchedCatalog;
  const existingSectionNames = useMemo(
    () => [...new Set((sections || []).map((name) => String(name || '').trim()).filter(Boolean))],
    [sections]
  );
  const sectionOptions = useMemo(() => {
    const names = [...existingSectionNames];
    (draft?.newProducts || []).forEach((product) => {
      const name = String(product.section || '').trim();
      if (name && !names.includes(name)) names.push(name);
    });
    return names;
  }, [existingSectionNames, draft?.newProducts]);
  const modalOpen = variant === 'modal' && open;
  const confirming = !!selectedProduct;

  useEffect(() => {
    if (parentCatalog.length > 0 || products.length > 0 || !catalogUrl) return;
    let cancelled = false;
    setLoadingCatalog(true);
    loadCatalog(catalogUrl).then((next) => {
      if (cancelled) return;
      setFetchedCatalog(next);
      setLoadingCatalog(false);
    });
    return () => {
      cancelled = true;
    };
  }, [parentCatalog.length, products.length, catalogUrl]);

  const resetForm = () => {
    setQuery('');
    setCategoryFilter('');
    setSelectedProduct(null);
    setQuantity(1);
    setQuantityDraft('1');
    setPlacement('');
    setNewSectionName('');
  };

  const close = () => {
    resetForm();
    if (onClose) onClose();
  };

  const goBack = () => {
    setSelectedProduct(null);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const timer = window.setTimeout(() => {
      if (!selectedProduct) inputRef.current?.focus();
    }, 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [modalOpen, selectedProduct]);

  const categories = useMemo(() => uniqueCatalogCategories(products), [products]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((product) => {
        if (categoryFilter && catalogCategoryKey(product.category) !== categoryFilter) return false;
        if (q && !(product.name || '').toLowerCase().includes(q)) return false;
        return true;
      })
      .map((product) => ({ ...product, name: String(product.name || '').trim() }));
  }, [products, query, categoryFilter]);

  const trimmed = query.trim();
  const exactMatch = matches.some((product) => (product.name || '').toLowerCase() === trimmed.toLowerCase());
  const showCustom = trimmed.length > 0 && !exactMatch;
  const selectedName = String(selectedProduct?.name || '').trim();
  const targetSection = placement === CREATE_NEW_SECTION ? newSectionName.trim() : placement;
  const pendingRequest = {
    name: selectedName,
    quantity,
    section: targetSection
  };
  const canAdd = canSubmitNewProductRequest(pendingRequest);

  const commitQuantity = (next) => {
    const parsed = parseInt(next, 10);
    const safe = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    setQuantity(safe);
    setQuantityDraft(String(safe));
  };

  const selectProduct = (product) => {
    const name = (product?.name || '').trim();
    if (!name) return;
    const parsedPrice = parseFloat(product?.price);
    setSelectedProduct({
      name,
      imageUrl: catalogImageUrl(product),
      price: Number.isFinite(parsedPrice) ? parsedPrice : '',
      dimensions: productDimensions(product),
      category: productCategory(product)
    });
  };

  const addSelected = () => {
    if (!canAdd) return;
    onAdd({
      name: selectedName,
      quantity,
      section: targetSection,
      existingSectionNames,
      sourceSection: sectionName || '',
      imageUrl: selectedProduct?.imageUrl || null,
      price: selectedProduct?.price,
      dimensions: selectedProduct?.dimensions || ''
    });
    close();
  };

  if (variant === 'list') {
    return <RequestedProductList products={existing} onRemove={onRemove} />;
  }

  if (!modalOpen) return null;

  return (
    <div
      className="no-print request-add-product-modal"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <style>{`
        .request-add-product-modal {
          position: fixed;
          inset: 0;
          z-index: 1200;
          background-color: rgba(44, 44, 44, 0.46);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 28px 24px;
          box-sizing: border-box;
        }
        .request-add-product-dialog {
          width: min(73vw, 1100px);
          height: min(84vh, 860px);
          max-height: calc(100vh - 56px);
          display: flex;
          flex-direction: column;
          background-color: #faf8f3;
          border: 1px solid #e8e0d4;
          border-radius: 4px;
          box-sizing: border-box;
          overflow: hidden;
        }
        .request-add-product-header {
          flex-shrink: 0;
          padding: 22px 24px 16px;
          background-color: #faf8f3;
        }
        .request-add-product-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          padding: 0 24px 24px;
        }
        .request-add-product-confirm {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(280px, 1fr);
          gap: 32px 40px;
          align-items: stretch;
          min-height: 100%;
        }
        .request-add-product-confirm-copy {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .request-add-product-confirm-kicker {
          font-family: ${fontSans};
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #8a847c;
          margin-bottom: 14px;
        }
        .request-add-product-confirm-copy .request-add-product-card-name {
          margin-top: 0;
          height: auto;
          font-size: 20px;
          line-height: 1.3;
          display: block;
          overflow: visible;
          -webkit-line-clamp: unset;
        }
        .request-add-product-confirm-copy .request-add-product-identity {
          gap: 6px;
          margin-top: 0;
        }
        .request-add-product-confirm-fields {
          display: flex;
          flex-direction: column;
          gap: 18px;
          margin-top: 28px;
        }
        .request-add-product-confirm-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
          margin-top: auto;
          padding-top: 32px;
        }
        .request-add-product-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 16px;
        }
        .request-add-product-toolbar input[type="text"] {
          flex: 1 1 auto;
        }
        .request-add-product-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px 14px;
          align-items: stretch;
        }
        .request-add-product-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          padding: 8px;
          margin: 0;
          border: 1px solid transparent;
          border-radius: 2px;
          background: transparent;
          cursor: pointer;
          text-align: left;
          transition: background-color 0.15s ease, border-color 0.15s ease;
        }
        .request-add-product-card:hover,
        .request-add-product-card:focus-visible {
          background-color: #f4f2ec;
          border-color: #e8e0d4;
        }
        .request-add-product-card-image,
        .request-add-product-confirm-image {
          width: 100%;
          background-color: #ece8df;
          border: 1px solid #e8e0d4;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .request-add-product-card-image {
          flex-shrink: 0;
          height: clamp(170px, 16vw, 210px);
        }
        .request-add-product-confirm-image {
          height: 100%;
          min-height: 380px;
          padding: 18px;
          box-sizing: border-box;
        }
        .request-add-product-card-image img,
        .request-add-product-confirm-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }
        .request-add-product-identity {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 14px;
          flex: 0 0 auto;
          font-size: 13px;
        }
        .request-add-product-card-name {
          font-size: 13px;
          font-weight: 400;
          line-height: 1.35;
          color: #2C2C2C;
          font-family: ${fontSans};
          text-transform: none;
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .request-add-product-card-meta {
          font-size: 12px;
          line-height: 1.35;
          color: #6b645c;
          font-family: ${fontSans};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .request-add-product-empty {
          padding: 36px 8px;
          font-size: 13px;
          color: #6b645c;
          font-family: ${fontSans};
        }
        .request-add-product-custom {
          height: auto;
          flex: 0 0 auto;
          margin-bottom: 16px;
        }
        .request-add-product-modal button:focus-visible {
          outline: 2px solid #2C2C2C;
          outline-offset: 2px;
        }
        @media (max-width: 1180px) {
          .request-add-product-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 900px) {
          .request-add-product-modal {
            padding: 16px 12px;
          }
          .request-add-product-dialog {
            width: min(94vw, 720px);
            height: min(86vh, 820px);
          }
          .request-add-product-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .request-add-product-toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          .request-add-product-confirm {
            grid-template-columns: minmax(0, 1.1fr) minmax(240px, 1fr);
            gap: 24px;
          }
        }
        @media (max-width: 560px) {
          .request-add-product-dialog {
            width: calc(100vw - 16px);
          }
          .request-add-product-header,
          .request-add-product-body {
            padding-left: 16px;
            padding-right: 16px;
          }
          .request-add-product-grid {
            grid-template-columns: 1fr;
          }
          .request-add-product-card-image {
            height: clamp(220px, 62vw, 300px);
          }
          .request-add-product-confirm {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .request-add-product-confirm-image {
            min-height: 260px;
          }
          .request-add-product-confirm-actions {
            justify-content: stretch;
          }
          .request-add-product-confirm-actions button {
            flex: 1 1 auto;
          }
        }
      `}</style>
      <div
        className="request-add-product-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${fieldId}-title`}
      >
        <div className="request-add-product-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={modalTitleStyle} id={`${fieldId}-title`}>
              Add a Product
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              style={{
                ...textActionStyle,
                fontSize: 22,
                lineHeight: 1,
                textDecoration: 'none',
                color: '#6b645c',
                padding: 4
              }}
            >
              ×
            </button>
          </div>

          {!confirming ? (
            <div className="request-add-product-toolbar">
              <input
                id={fieldId}
                ref={inputRef}
                type="text"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (matches[0]) selectProduct(matches[0]);
                  else if (showCustom) selectProduct({ name: trimmed, imageUrl: null });
                }}
                placeholder="Search products…"
                className="mayker-field"
                style={{
                  ...compactInputStyle,
                  width: '100%'
                }}
              />
              {categories.length > 1 ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Category</span>
                  <select
                    aria-label="Category"
                    className="mayker-field"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{
                      ...compactInputStyle,
                      minWidth: 148,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">All</option>
                    {categories.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="request-add-product-body">
          {confirming ? (
            <div className="request-add-product-confirm">
              <CatalogImage
                className="request-add-product-confirm-image"
                src={selectedProduct.imageUrl}
                alt={selectedName}
              />
              <div className="request-add-product-confirm-copy">
                <div className="request-add-product-confirm-kicker">Selected product</div>
                <ProductIdentity
                  name={selectedName}
                  dimensions={selectedProduct.dimensions}
                />

                <div className="request-add-product-confirm-fields">
                  <div>
                    <span style={fieldLabelStyle}>Quantity</span>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        disabled={quantity <= 1}
                        onClick={() => commitQuantity(quantity - 1)}
                        style={{
                          ...stepperButtonStyle,
                          opacity: quantity <= 1 ? 0.35 : 1,
                          cursor: quantity <= 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        −
                      </button>
                      <input
                        type="text"
                        className="mayker-field"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        aria-label="Requested quantity"
                        value={quantityDraft}
                        onChange={(e) => {
                          const next = e.target.value.replace(/[^\d]/g, '');
                          setQuantityDraft(next);
                          const parsed = parseInt(next, 10);
                          if (Number.isFinite(parsed) && parsed >= 1) setQuantity(parsed);
                        }}
                        onFocus={(e) => e.target.select()}
                        onBlur={() => commitQuantity(quantityDraft)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitQuantity(quantityDraft);
                          }
                          if (e.key === 'Escape') {
                            setQuantityDraft(String(quantity));
                            e.currentTarget.blur();
                          }
                        }}
                        style={{
                          width: 36,
                          height: 28,
                          padding: 0,
                          border: '1px solid #e8e4dc',
                          borderRadius: 2,
                          backgroundColor: '#FFFFFF',
                          color: '#2C2C2C',
                          fontSize: 13,
                          fontFamily: fontSans,
                          lineHeight: '28px',
                          textAlign: 'center',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => commitQuantity(quantity + 1)}
                        style={stepperButtonStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`${fieldId}-section`} style={fieldLabelStyle}>Add to</label>
                    <select
                      id={`${fieldId}-section`}
                      className="mayker-field"
                      value={placement}
                      onChange={(e) => setPlacement(e.target.value)}
                      style={{
                        ...compactInputStyle,
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select a section</option>
                      {sectionOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                      <option value={CREATE_NEW_SECTION}>+ Create new section</option>
                    </select>
                  </div>

                  {placement === CREATE_NEW_SECTION ? (
                    <div>
                      <label htmlFor={`${fieldId}-new-section`} style={fieldLabelStyle}>Section name</label>
                      <input
                        id={`${fieldId}-new-section`}
                        className="mayker-field"
                        type="text"
                        value={newSectionName}
                        onChange={(e) => setNewSectionName(e.target.value)}
                        placeholder="Enter section name…"
                        style={{
                          ...compactInputStyle,
                          width: '100%'
                        }}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="request-add-product-confirm-actions">
                  <button
                    type="button"
                    onClick={goBack}
                    style={headerButtonStyle}
                  >
                    ← Back to products
                  </button>
                  <button
                    type="button"
                    onClick={addSelected}
                    disabled={!canAdd}
                    style={canAdd ? primaryHeaderButtonStyle : disabledPrimaryStyle}
                  >
                    Add Product
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {showCustom ? (
                <button
                  type="button"
                  className="request-add-product-card request-add-product-custom"
                  onClick={() => selectProduct({ name: trimmed, imageUrl: null })}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: warmHeaderBg,
                    border: '1px solid #ece4d6'
                  }}
                >
                  <div className="request-add-product-card-name">Add “{trimmed}”</div>
                  <div className="request-add-product-card-meta">Custom product request</div>
                </button>
              ) : null}

              {loadingCatalog && matches.length === 0 ? (
                <div className="request-add-product-empty">Loading catalog…</div>
              ) : null}

              {!loadingCatalog && matches.length === 0 && !showCustom ? (
                <div className="request-add-product-empty">
                  {trimmed ? 'No matching products' : 'No products available'}
                </div>
              ) : null}

              {matches.length > 0 ? (
                <div className="request-add-product-grid">
                  {matches.map((product, idx) => (
                    <button
                      key={`${product.name}-${idx}`}
                      type="button"
                      className="request-add-product-card"
                      onClick={() => selectProduct(product)}
                    >
                      <CatalogImage
                        className="request-add-product-card-image"
                        src={catalogImageUrl(product)}
                        alt=""
                      />
                      <ProductIdentity
                        name={product.name}
                        dimensions={productDimensions(product)}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>

      </div>
    </div>
  );
}
